// @ts-check
"use strict"

import * as Sentry from "@sentry/react";

/**
 * Global Audio Manager to handle TTS audio playback and STT interaction
 */
class AudioManager {
  constructor() {
    this.currentAudio = null;
    this.wasPlayingBeforeSTT = false;
    this.audioQueue = [];
    this.isSTTActive = false;
  }

  /**
   * Play TTS audio and manage playback
   * @param {Blob} audioBlob - The audio blob to play
   * @param {Object} options - Playback options
   * @param {boolean} options.autoPlay - Whether to auto-play the audio
   * @param {number} options.volume - Audio volume (0-1)
   * @returns {HTMLAudioElement} - The audio element
   */
  playTTS(audioBlob, options = { autoPlay: true, volume: 1 }) {
    // Stop and cleanup any currently playing audio
    this.stopCurrent();

    // Create audio element
    const audio = new Audio();
    audio.src = URL.createObjectURL(audioBlob);
    audio.volume = options.volume;

    // CRITICAL FIX: Add preload to ensure audio is ready
    audio.preload = 'auto';

    // Store as current audio
    this.currentAudio = audio;

    // Auto-play if requested and STT is not active
    if (options.autoPlay && !this.isSTTActive) {
      // CRITICAL FIX: Enhanced first-time audio playback handling
      const playAudio = async () => {
        try {
          await audio.play();
        } catch (error) {
          Sentry.captureException(error, {
            extra: { 
              context: 'AudioManager - initial audio play attempt',
              autoPlay: options.autoPlay,
              volume: options.volume,
              isSTTActive: this.isSTTActive,
              audioSrc: audio.src?.substring(0, 100)
            }
          });
          
          // CRITICAL FIX: For first-time audio issues, try to play after a short delay
          setTimeout(async () => {
            try {
              await audio.play();
            } catch (retryError) {
              Sentry.captureException(retryError, {
                extra: { 
                  context: 'AudioManager - retry audio play attempt',
                  originalError: error.message,
                  autoPlay: options.autoPlay,
                  volume: options.volume,
                  isSTTActive: this.isSTTActive
                }
              });
              console.error('AudioManager:  Audio play failed completely:', retryError);
              // Don't throw - let the system handle gracefully
            }
          }, 100);
        }
      };

      playAudio();
    }

    // Cleanup when audio ends
    audio.addEventListener('ended', () => {
      this.cleanupAudio(audio);
    });

    // CRITICAL FIX: Add error handling for audio loading issues
    audio.addEventListener('error', (error) => {
      this.cleanupAudio(audio);
    });

    

    return audio;
  }

  /**
   * Stop current audio playback completely
   */
  stopCurrent() {
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio.currentTime = 0;
      this.cleanupAudio(this.currentAudio);
    }
  }

  /**
   * Pause current audio playback (without stopping)
   */
  pauseCurrent() {
    if (this.currentAudio && !this.currentAudio.paused) {
      this.currentAudio.pause();
    }
  }

  /**
   * Resume current audio playback
   */
  resumeCurrent() {
    if (this.currentAudio && this.currentAudio.paused && !this.isSTTActive) {
      this.currentAudio.play().catch(error => {
        console.warn('AudioManager: Failed to resume audio:', error);
      });
    }
  }

  /**
   * Pause TTS audio when STT starts
   */
  pauseForSTT() {
    this.isSTTActive = true;

    if (this.currentAudio && !this.currentAudio.paused) {
      this.wasPlayingBeforeSTT = true;
      this.currentAudio.pause();
      console.log('AudioManager: Paused TTS for STT');
    }
  }

  /**
   * Resume TTS audio when STT stops
   */
  resumeAfterSTT() {
    this.isSTTActive = false;

    if (this.wasPlayingBeforeSTT && this.currentAudio) {
      this.currentAudio.play().catch(error => {
        console.warn('AudioManager: Failed to resume TTS after STT:', error);
      });
      this.wasPlayingBeforeSTT = false;
    }
  }

  /**
   * Stop all audio playback
   */
  stopAll() {
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio.currentTime = 0;
      this.cleanupAudio(this.currentAudio);
    }
    this.wasPlayingBeforeSTT = false;
    this.isSTTActive = false;
  }

  /**
   * Cleanup audio element and revoke object URL
   * @param {HTMLAudioElement} audio - The audio element to cleanup
   */
  cleanupAudio(audio) {
    if (audio.src && audio.src.startsWith('blob:')) {
      URL.revokeObjectURL(audio.src);
    }

    if (this.currentAudio === audio) {
      this.currentAudio = null;
    }
  }

  /**
   * Get current audio state
   * @returns {Object} - Current audio state
   */
  getState() {
    return {
      hasCurrentAudio: !!this.currentAudio,
      isPlaying: this.currentAudio ? !this.currentAudio.paused : false,
      isSTTActive: this.isSTTActive,
      wasPlayingBeforeSTT: this.wasPlayingBeforeSTT,
      currentTime: this.currentAudio ? this.currentAudio.currentTime : 0,
      duration: this.currentAudio ? this.currentAudio.duration : 0
    };
  }

  /**
   * Set volume for current audio
   * @param {number} volume - Volume level (0-1)
   */
  setVolume(volume) {
    if (this.currentAudio) {
      this.currentAudio.volume = Math.max(0, Math.min(1, volume));
    }
  }

  /**
   * Debug method to log current state
   */
}

// Create singleton instance
const audioManager = new AudioManager();

export default audioManager;